import { AuthContextProps, ErrorContext, useAuth } from "react-oidc-context";
import { CUSTOM_METHOD, HOSTED_UI_REDIRECT } from "./auth/utils";
import { useCustomAuth, useCustomAuthInterface } from "./auth/useCustomAuth";
import { User } from "oidc-client-ts";
import { useEffect, useRef, useState } from "react";
import { FetchBaseQueryError, skipToken } from "@reduxjs/toolkit/query";
import { useGetLoggedInUserProfileQuery } from "../features/profilesSlice";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "./store";
import { SerializedError } from "@reduxjs/toolkit";
import { baseName } from "..";
import { useLogToBackEndMutation } from "../features/utilitySlice";
import { printError, toStringRepresentation } from "./utils";
import { getVersion, useAppDispatch, useAppSelector } from "./hooks";
import { clearSessionRTK, CustomSession, selectAuthMethod, selectIsAuthenticated, selectIsAuthReady, selectIsMagicLogin, setAuthenticated, setSessionForRTK } from "../features/userTokenManagementSlice";
import { useProcessClearOAuthMutation } from "../components/healthwallet/healthWalletSlice";
//import { computeVerificationStatus } from "../components/healthwallet/onboardingUtils";
import type { ClearStatus } from "../types/onboarding";


const checkNetwork = async () => {
    try {
        const testUrl = 'https://www.polygonhealth.com/map/message.svg'//https://www.google.com/favicon.ico
        await fetch(testUrl, { mode: 'no-cors', cache: 'no-store' }); // A small, fast resource
        return true;
    } catch (e) {
        return true;
        //return true;//false;
    }
};


// Stable at module load: true if the URL contained a magic-link token (?t=)
// when the app first loaded. Used downstream so consumers can gate ChatService
// construction (and similar) until the magic-link login flow has resolved.
// Not affected by later navigation that strips ?t= from the URL.
const INITIAL_MAGIC_LINK_TOKEN: string | null =
    (typeof window !== 'undefined')
        ? new URLSearchParams(window.location.search).get('t')
        : null;
export const IS_MAGIC_LINK_LOGIN: boolean = !!INITIAL_MAGIC_LINK_TOKEN;

export interface AuthBootstrapResult {

    isSuccess: boolean,//authReady:
    isLoading: boolean,
    isError: boolean,
    // Auth
    authStatus: string | null,
    auth: AuthContextProps;
    authUser: User | null | undefined;
    sub: string,
    isAuthenticated: boolean;
    authMethod: typeof CUSTOM_METHOD | typeof HOSTED_UI_REDIRECT | null;
    customAuth: useCustomAuthInterface;
    // True once MagicLinkGuard detects a ?t= magic-link token at first
    // mount. Stable for the lifetime of the page; doesn't flip back when
    // the URL is later cleaned. Consumers couple this with a login-success
    // signal to decide whether they're still waiting for magic-link auth.
    isMagicLogin: boolean;

    // Profil
    profile: any;
    isProfileLoading: boolean;
    isProfileSuccess: boolean;
    profileError: any;

    // Persistent delete-in-progress flag (dialogSlice) — true from delete-confirm through signout; keeps the
    // delete modal in a stable state across the auth-churn remounts.
    deleteTriggered: boolean;

    // Errors
    error: ErrorContext | undefined | FetchBaseQueryError | SerializedError;

    // Actions
    logoutFromAnyAuthMethod: () => Promise<void>;
    clearLocalSession: () => Promise<void>;
}


export function useAuthBootstrap(isJustLoggedOut?: boolean): AuthBootstrapResult {
    const dispatch = useAppDispatch()
    const isAithenticatedRTK = useSelector(selectIsAuthenticated)
    const isMagicLogin = useSelector(selectIsMagicLogin)
    const [processClearOAuth, { data, isLoading: isProcessClearOAuthLoading, isSuccess: isProcessClearOAuthSuccess, isError: isErrorProcessClearOAuth, error: processClearOAuthError }] = useProcessClearOAuthMutation();
    /* ---------------------------------- */
    /* 1. CORE AUTH OWNERSHIP              */
    /* ---------------------------------- */

    const auth = useAuth();              // 🔑 ONLY PLACE
    const customAuth = useCustomAuth();

    const {
        user: oidcUser,
        isAuthenticated: oidcIsAuthenticated,
        isLoading: oidcIsLoading,
        error: authOIDCError,
    } = auth;



    /* ---------------------------------- */
    /* 2. AUTH INITIALIZATION PHASE        */
    /* ---------------------------------- */


    const initializedRef = useRef(false);
    //const [networkErrorOccurred, setNetworkErrorOccurred] = useState(false);
    const [renewErrorOccurred, setRenewErrorOccurred] = useState<string | undefined>(undefined);
    const [isLoadingAuth, setIsLoadingAuth] = useState(true);
    const [authReady, setAuthReady] = useState(false);

    
    useEffect(() => {
        if (initializedRef.current) return;

        // 1️⃣ Custom auth wins immediately
        if (customAuth.session) {
            console.log("AuthInitializer: Custom auth detected. Skipping OIDC bootstrap.");
            // setIsLoadingAuth(false);
            // setAuthReady(true);
            //return;
        }
        // 2️⃣ Explicit logout wins
        if (isJustLoggedOut) {
            console.log("AuthInitializer: Just logged out. Skipping silent sign-in.");
            setIsLoadingAuth(false);
            setAuthReady(true);
            return;
        }

        // 3️⃣ Nothing to do yet
        if (auth.isLoading || customAuth.isLoading) return;
        initializedRef.current = true;
        // 4️⃣ Attempt silent sign-in only if meaningful
        // if (!auth.user || !auth.user.expired) {
        //     setIsLoadingAuth(false);
        //     setAuthReady(true)//!!auth.user);
        //     return;
        // }
        // ///////////////////////???????????????????????????????????????????????
        // if (auth.isAuthenticated && auth.user && !auth.user.expired) {
        //     const regularUser = auth.user
        //     const newSession: CustomSession = { sub: regularUser?.profile?.sub, expires_at: regularUser.expires_at, profile: regularUser.profile, id_token: regularUser?.id_token!, access_token: regularUser?.access_token }
        //     dispatch(setSessionForRTK({ newSession, authMethod: HOSTED_UI_REDIRECT }))
        //     dispatch(setAuthenticated(true))
        //     //dispatch(setAuthReady(true));
        // }


        // 4️⃣ Attempt silent sign-in only if meaningful
        if ((!auth.user || !auth.user.expired) && (!customAuth.session || !customAuth.isExpired())) {
            setIsLoadingAuth(false);
            setAuthReady(true)//!!auth.user);         
            return;
        }

        (async () => {
            // // const isOnline = await checkNetwork();
            // // if (!isOnline ) {
            // //setNetworkErrorOccurred(true);
            // setRenewErrorOccurred("Network Error Occurred")
            // setIsLoadingAuth(false);
            // setAuthReady(true)//to indicate auth is done albait unsuccessfully, so unathenticated profile 361 could be shown
            // return;
            // // }
            // 4️⃣ Attempt silent sign-in only if meaningful
            if (customAuth.session && customAuth.isExpired()) {
                const sessionRet = await customAuth.renewIfExpired() //logs out on fail //catches exceptions
                console.info(`customAuth.renewIfExpired() returned`, sessionRet)
                if (!sessionRet.success) {
                    setRenewErrorOccurred("customAuth renewIfExpired Error Occurred")
                }
                setIsLoadingAuth(false);
                console.log('isSuccessRenew:', customAuth.isSuccessRenew)
                setAuthReady(true)
                return
            }
            // 4️⃣ Attempt silent sign-in only if meaningful
            if (!auth.user || !auth.user?.expired) {
                setIsLoadingAuth(false);
                setAuthReady(true)//!!auth.user);
                return;
            }
            if (auth.user && auth.user?.expired) {
                try {
                    const user = await auth.signinSilent();
                    // in finally
                    //setIsLoadingAuth(false);
                    //setAuthReady(true)//!!auth.user);
                } catch (err) {
                    //not an error for us - just cant log in
                    //authError = authError || authError
                    const errMsg = `auth Silent sign-in failed: ${err}`
                    console.error(errMsg);
                    setRenewErrorOccurred(errMsg)
                    auth.removeUser()
                    auth.clearStaleState()
                } finally {
                    setIsLoadingAuth(false);
                    setAuthReady(true)//to indicate auth is done albait unsuccessfully, so unathenticated profile 361 could be shown
                }
                return
            }
            setIsLoadingAuth(false);
            setAuthReady(true)//!!auth.user);
        })();
    }, [auth.isLoading, auth.user, customAuth.isLoading /*customAuth.session*/, isJustLoggedOut]);

    //  return { isSuccess, isLoading, networkErrorOccurred };
    ;


    /* ---------------------------------- */
    /* 3. PROFILE QUERY (HARD GATED)       */
    /* ---------------------------------- */

    const authUser = customAuth.user ?? oidcUser;
    const isAuthenticated = customAuth.isAuthenticated || oidcIsAuthenticated;
    const authMethod = customAuth.isAuthenticated ? CUSTOM_METHOD : oidcIsAuthenticated ? HOSTED_UI_REDIRECT : null;

    const sub = customAuth.user?.sub ?? authUser?.profile?.sub ?? null;
    // Read email/phone SHAPE-AWARE and send BOTH to getLoggedInUserProfile (it matches on either).
    // - federated (oidcUser): claims are nested under `.profile`
    // - custom/magic-link (customAuth.user === session.profile): claims are top-level (the exchange's decoded ID token)
    // The old `authUser?.profile?.email` read `session.profile.profile.email` (undefined) for custom sessions → guest 361.
    const email = oidcUser?.profile?.email ?? customAuth.user?.email;
    const nickname = authUser?.profile?.nickname ?? ''
    const phone = customAuth.user?.phone_number ?? oidcUser?.profile?.phone_number;
    const authError = authOIDCError || customAuth.error || renewErrorOccurred


    //const shouldFetchProfile =        authReady &&        isAuthenticated &&        !!sub;
    // const profileArg = authReady //shouldFetchProfile

    const isFederated: boolean | null = authUser?.profile ?
        (authUser.profile["cognito:username"] as string).toLowerCase().includes('facebook_') ? true :
            (authUser?.profile["cognito:username"] as string).toLowerCase().includes('google_') ? true :
                (authUser?.profile["cognito:username"] as string).toLowerCase().includes('signinwithapple_') ? true : false : false// idTokenClaims?.identities?.[0]?.providerName.includes('cognito-idp')//userAttributesState.userAttributes ? userAttributesState.userAttributes.isFederated ?? null : null


    const isDelteProfileStatus = useSelector((state: RootState) => {
        const foundKey = state.profileApi.mutations ? Object.keys(state.profileApi.mutations).find(mkey => state.profileApi.mutations[mkey]?.endpointName === 'DeleteUserData') : undefined
        //console.log(`isDelteProfileLoading  status: ${foundKey ? state.profileApi.mutations[foundKey]?.status : 'key not found'} `)
        return foundKey ? state.profileApi.mutations[foundKey]?.status : 'uninitialized'//=== "pending"
    });

    // Persistent delete-in-progress flag (dialogSlice) — set when the user confirms deletion. Survives the
    // ConfirmDeleteProfile remount that clears the ephemeral isDelteProfileStatus. Exposed on the return so the
    // delete modal can hold a stable state; nothing else reads it (the interface field was previously undefined).
    const deleteTriggered = useSelector((state: RootState) => state.dialog.deleteTriggered)

    var getUserProfileArg: any// 'unique symbol | profileCreateMatch'
    if (authReady && !isLoadingAuth) {
        if (!isAuthenticated || !authUser || !sub || (!email && !phone) || isDelteProfileStatus === 'pending') {
            //load guest profile
            getUserProfileArg = {
                iamId: 'unauthenticated',
                nickname: 'Guest', //will be updated by the backend
                email: '', //will be updated by the backend''
                //zipCode: user ? email : null,
                isFederated: false//will be updated by the backend''
            }
        } else {
            //TODO set headers here too ( bearer)
            getUserProfileArg = {
                iamId: sub ?? "unauthenticated",
                nickname: nickname,// user ? nickname : userAttributesState.userAttributes?.nickname ?? null,
                email: email,//user ? email : userAttributesState.userAttributes?.email ?? null,
                phone: phone,//zipCode: user ? email : null,
                isFederated: isFederated,
                sms_consent: authMethod === CUSTOM_METHOD  //all SMS logins consented
            }
            //setAuthToken({ token: user?.access_token, sub: isAuthenticated ? user?.profile?.sub : 'unauthenticated' })
        }
    }
    else {
        getUserProfileArg = skipToken
    }
    const { data: profile, isLoading: isLoadingProfile, isSuccess: isProfileSuccess, isError: isProfileError, error: profileError, refetch: profileRefetch } = useGetLoggedInUserProfileQuery(getUserProfileArg)

    // Interim (pre-shell-decomposition): when the resolved profile id CHANGES — login → real id, or
    // logout(-no-redirect) → guest 361 — nudge the messaging poller to (re)fetch chat state, so the
    // unread badge fills in on login and resets on logout regardless of the page / whether a chat surface
    // is even mounted. (Magic-link's isAuthResolving gate otherwise suppresses the usual serviceFactory
    // -driven trigger, and the always-on journal mini-chat isn't mounted when its floating panel is
    // minimized.) Poked via the GLOBAL runtime handle, not an import: importing the messaging module here
    // would create a circular dep (AuthBootstrap→ChatScopeUseChat→app/hooks→AuthBootstrapProvider→
    // AuthBootstrap) with load-time side effects — and this seam prefigures the future auth→messaging
    // EVENT boundary once the map decomposes into shell + tools ([[project_map_polygon_shell]]).
    // getUserId() guard skips redundant/same-user calls (incl. regular login, where the ChatService
    // construction already reset the poller). resetForUser(361) on logout disposes the prior user's
    // polling and re-fetches as guest — symmetric with the messaging component's own rebuild path.
    useEffect(() => {
        const id = profile?.id;
        const poller = (globalThis as any).__pollingChatDataService;
        if (id && poller && poller.getUserId?.() !== id) {
            poller.resetForUser(id);
        }
    }, [profile?.id]);
    
    useEffect(() => {
        //Need to setSessionForRTK so tokens can be accessed in qryFuncWithSubHeader 
        if (isProfileSuccess && authReady) {
            if (getUserProfileArg?.iamId !== 'unauthenticated' && auth.isAuthenticated && auth.user && !auth.user?.expired) {
                const regularUser = auth.user
                const newSession: CustomSession = { sub: regularUser?.profile?.sub, expires_at: regularUser.expires_at, profile: regularUser.profile, id_token: regularUser?.id_token!, access_token: regularUser?.access_token }
                dispatch(setSessionForRTK({ newSession, authMethod: HOSTED_UI_REDIRECT }))
                dispatch(setAuthenticated(true))
                //dispatch(setAuthReady(true));
                console.log('AuthBootstrap: OIDC auth detected, session RTK session set created');
            } else if (authMethod === CUSTOM_METHOD && customAuth.isAuthenticated && customAuth.session) {
                // Custom auth (SMS/Cognito)
                console.log('AuthBootstrap: Custom auth detected, session already set during login');
                // const newSession: CustomSession = { 
                //     sub: customAuth.session.profile?.sub, 
                //     expires_at: customAuth.session.expires_at, 
                //     profile: customAuth.session.profile, 
                //     id_token: customAuth.session.id_token, 
                //     access_token: customAuth.session.access_token 
                // }
                // dispatch(setSessionForRTK({ newSession, authMethod: CUSTOM_METHOD }))
                // dispatch(setAuthenticated(true))
            } else {
                dispatch(clearSessionRTK()) //resets isAuthReadyRTK
            }
        }
        if (authReady && !isProfileSuccess && isProfileError && profileError) {
            dispatch(clearSessionRTK()); //dispatch(setAuthenticated(false));
            //     }
            //     ///////////logging
            //     //if (isError && errorAuthenticator && !isSignOutErrorButLoggedIn) {
            // if (isProfileError && profileError) {
            const profileErrorMsg = printError(profileError)
            //'User not found in authentication system. Must log out
            if (profileErrorMsg.includes('User not found in authentication system. Must log out')) {
                (async () => {
                    await logoutFromAnyAuthMethod()
                })()
            }
            const keyVialoation = profileErrorMsg && profileErrorMsg.includes("Key")
            if (keyVialoation) {
                logToBackEnd({ context: 'profileError-keyVialoation', message: toStringRepresentation({ ...profileError, sub/*: user?.profile?.sub ?? 'none' */ }, { status: 'ERROR' }), version: getVersion() });
            }
        }

    }, [isProfileSuccess, isProfileError, profileError, authReady, auth.isAuthenticated, auth.user, auth.user?.expired])


    // Intercept CLEAR OAuth callbacks before oidc-client-ts mishandles them.
    // ?code=&state=&scope= are standard OAuth2 params — oidc-client-ts watches for
    // them and will attempt to process them as a Cognito/OIDC callback if we don't
    // consume them first. We exchange them with CLEAR's backend here, then strip them
    // from the URL via window.location.replace. The replace also reloads the page,
    // which is intentional: it gives oidc-client-ts a clean URL to initialize against
    // so it doesn't end up in a bad state from having seen params it can't process.
    //
    // PHASE 1 device-proof gate: the `?clear-result=success` URL stamping (and its
    // post-reload read) is removed. The server now sets a `trusted_device` cookie at
    // CLEAR exchange success and derives `clearVerifiedInFlow` from the JSONB
    // `setAt`, so the post-reload /onboarding/status response carries the flag
    // directly. See HW_CLEAR_PROOF_COOKIE_SPEC.md.
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);

        (async () => {
            const isHealthWalletPath = window.location.pathname.includes('/healthwallet');
            const isEpicHealthWalletPath = window.location.pathname.includes('/epic');
            const code = urlParams.get('code');
            const state = urlParams.get('state');

            if (isHealthWalletPath && code) {
                console.log('AuthInitializer: CLEAR OAuth callback detected on HealthWallet path:', { code: code.substring(0, 10) + '...', state });
                // The exchange returns the FULL original frontend URL (with the condition
                // segment). Epic strips the condition for its single whitelisted redirect,
                // so Epic lands us on /map/healthwallet/ (no condition) — we use this to
                // retarget the forced redirect below back to /map/<condition>/healthwallet/.
                // CLEAR isn't stripped, so it equals the current URL (no-op). See
                // project_epic_redirect_uri_design.
                let frontendRedirectUri: string | undefined;
                try {
                    const result = await processClearOAuth({ code: code!, state: state!, profileId: profile?.id }).unwrap();
                    if (result.processed && result.clear_status) {
                        console.log('VerificationWrapper: CLEAR verification result:', result.clear_status, result);
                    }
                    frontendRedirectUri = (result as any)?.frontend_redirect_uri ?? undefined;
                } catch (err) {
                    console.error(err);
                }
                // Build the post-exchange target. We strip CLEAR's/Epic's OAuth params and
                // reload — the reload is required to reset oidc-client-ts, which has already
                // seen ?code= in memory this page lifecycle. The trusted_device cookie set by
                // processClearOAuth survives (HttpOnly); post-reload /onboarding/status carries
                // clearVerifiedInFlow server-derived from the JSONB.
                //
                // Prefer the server-supplied frontend_redirect_uri (restores the condition
                // segment Epic stripped); fall back to the current path. Same-origin guard
                // prevents an open redirect if the stored URL is ever unexpected.
                let newUrl: URL;
                try {
                    const candidate = frontendRedirectUri
                        ? new URL(frontendRedirectUri, window.location.origin)
                        : new URL(window.location.pathname + window.location.search, window.location.origin);
                    newUrl = candidate.origin === window.location.origin
                        ? candidate
                        : new URL(window.location.pathname + window.location.search, window.location.origin);
                } catch {
                    newUrl = new URL(window.location.pathname + window.location.search, window.location.origin);
                }
                newUrl.searchParams.delete('code');
                newUrl.searchParams.delete('state');
                newUrl.searchParams.delete('scope');
                console.log('AuthInitializer: Redirecting to:', newUrl.toString());
                window.location.replace(newUrl.toString());
            }
        })();
    }, []); // Run once on mount

    /* ---------------------------------- */
    /* 4. ACTIONS                          */
    /* ---------------------------------- */

    const logoutFromAnyAuthMethod = async () => {
        if (authMethod === CUSTOM_METHOD) {//'sms-cognito'){
            await customAuth.logout()
        } else {
            await auth.removeUser();
            auth.signoutRedirect({
                state: { baseName },
                post_logout_redirect_uri: auth.settings?.post_logout_redirect_uri,
                extraQueryParams: {
                    client_id: auth.settings?.client_id,//"7amp8un1r51k9p7qqj00jovh75",
                    redirect_uri: auth.settings?.post_logout_redirect_uri ?? false,//"http://localhost:4000/map/admin/out/",
                    logout_uri: auth.settings?.post_logout_redirect_uri ?? false,//"http://localhost:4000/map/admin/out/",
                    response_type: "code",
                    state_data: baseName
                },
            })
        }
    }


    const clearLocalSession = async () => {
        customAuth.clearLocalAndRTKSesstion();
        await auth.removeUser();
        await auth.clearStaleState();
    };

    const { events: eventsAuthenticator } = auth
    const [logoutMessage, setLogoutMessage] = useState<{ message: string, hasBeenLoggedOut: boolean } | null>(null);
    const [logToBackEnd, { }] = useLogToBackEndMutation();
    ///////////events
    useEffect(() => {
        if (!eventsAuthenticator) {
            console.log(" eventsAuthenticator is not ready yet");
            return;
        }
        const silentRenewErrorSubscription = eventsAuthenticator.addSilentRenewError((err: Error) => {
            console.error('Detected Silent Renew Error:', err);
            // This indicates that the background attempt to refresh the session failed.
            // The user's stored refresh token is likely invalid or expired.
            setLogoutMessage(
                { message: "Your session has expired or become invalid. Please log in again to continue.", hasBeenLoggedOut: true }
            );
            // At this point, `isAuthenticated` will likely become `false` automatically
            // by oidc-client-ts, so we don't need to force a logout redirect.
            //logToBackEnd({ context: 'SilentRenewError', message: `${err}`, version: `${process.env.REACT_APP_VERSION} ${process.env.REACT_APP_BUILD_TIME ? new Date(process.env.REACT_APP_BUILD_TIME).toLocaleString(undefined, { dateStyle: 'long', timeStyle: 'short', }) : 'Unknown'}` });
            logToBackEnd({ context: 'SilentRenewError', message: toStringRepresentation(err, { status: 'ERROR' }), version: getVersion() });

        });
        // 2) Successful User Load (i.e. “user just signed in”)
        const removeUserLoaded = eventsAuthenticator.addUserLoaded((user) => {
            console.log('User Loaded (successful login):', user);
            //logToBackEnd({ context: 'UserLoaded', message: `User: ${user?.profile?.sub ?? 'none'}: Auth OK`, version: `${process.env.REACT_APP_VERSION} ${process.env.REACT_APP_BUILD_TIME ? new Date(process.env.REACT_APP_BUILD_TIME).toLocaleString(undefined, { dateStyle: 'long', timeStyle: 'short', }) : 'Unknown'}` });
            logToBackEnd({ context: 'UserLoaded', message: toStringRepresentation(`User: ${user?.profile?.sub ?? 'none'}: Auth OK`, { status: 'INFO' }), version: getVersion() });
        });

        // 3) (Optional) Catch “user signed out” as well
        const removeUserSignedOut = eventsAuthenticator.addUserSignedOut(() => {
            console.log('User Signed Out event');
            // Reset any UI you want on sign-out:
            logToBackEnd({ context: 'UserSignedOut', message: toStringRepresentation(`User: none': Log out OK`, { status: 'INFO' }), version: getVersion() });
        });

        //Cleanup the subscription when the component unmounts
        return () => {
            silentRenewErrorSubscription();
            removeUserLoaded();
            removeUserSignedOut();
        };
    }, [eventsAuthenticator, logToBackEnd, setLogoutMessage]); // Depend on 'events' object




    const authMethod1 = useAppSelector(selectAuthMethod)



    /* ---------------------------------- */
    /* 5. RETURN STABLE CONTRACT           */
    /* ---------------------------------- */

    const isAuthenticatedRTK = useAppSelector(selectIsAuthenticated)
    const isAuthReadyRTK = useAppSelector(selectIsAuthReady)


    return {
        //authReady,
        isError: isProfileError || authError,
        error: authError || profileError,
        isSuccess: (authReady && isAuthReadyRTK && isProfileSuccess),
        authStatus: (authReady && isAuthReadyRTK && isAuthenticated && isProfileSuccess) ? ((authUser && sub) ? 'authenticated' : 'unauthenticated') : 'unauthenticated',//null,
        isLoading: isLoadingProfile || isLoadingAuth || !isAuthReadyRTK,
        auth,
        authUser,
        isAuthenticated: isAithenticatedRTK,
        authMethod,
        customAuth,
        sub,
        isMagicLogin,
        profile,
        isProfileLoading: isLoadingProfile,
        isProfileSuccess,
        profileError,
        deleteTriggered,
        logoutFromAnyAuthMethod,
        clearLocalSession,
    };

}
